import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../api/client.js";
import "../Style/Testimonials.css";
import reviewBackground from "../assets/NewPhotos/HomeCoverPhoto.png";
import riseBeautiful from "../assets/NewPhotos/RiseBeautifull.png";
import { useHorizontalSlider } from "../hooks/useHorizontalSlider.js";

const LOOP_GROUPS = ["before", "current", "after"];
const LOOP_BOUNDARY_THRESHOLD = 2;
const EMPTY_FEEDBACK = {
  name: "",
  content: "",
  rating: 5,
};

const getLoopOffset = (scrollLeft, groupWidth) =>
  ((scrollLeft - groupWidth) % groupWidth + groupWidth) % groupWidth;

function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [feedback, setFeedback] = useState(EMPTY_FEEDBACK);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackNotice, setFeedbackNotice] = useState("");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const infiniteEnabled = testimonials.length >= 3;
  const renderedTestimonials = useMemo(() => {
    if (!infiniteEnabled) {
      return testimonials.map((testimonial, index) => ({
        testimonial,
        originalIndex: index,
        key: `single-${testimonial.id || index}-${index}`,
      }));
    }

    return LOOP_GROUPS.flatMap((group, groupIndex) =>
      testimonials.map((testimonial, index) => ({
        testimonial,
        groupIndex,
        originalIndex: index,
        key: `${group}-${testimonial.id || index}-${index}`,
      })),
    );
  }, [infiniteEnabled, testimonials]);
  const slider = useHorizontalSlider(renderedTestimonials.length);
  const loopWidthRef = useRef(0);
  const correctionFrameRef = useRef(null);
  const restoreFrameRef = useRef(null);
  const activeFrameRef = useRef(null);
  const isCorrectingScrollRef = useRef(false);
  const scrollStyleRestoreRef = useRef(null);
  const activeCardRef = useRef(null);

  const updateActiveCard = useCallback(() => {
    const track = slider.trackRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll("[data-slider-item]"));
    if (cards.length === 0) return;

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    const closestIndex = cards.reduce(
      (closest, card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(trackCenter - cardCenter);

        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    ).index;
    const nextActiveCard = cards[closestIndex];

    if (activeCardRef.current === nextActiveCard) return;

    activeCardRef.current?.classList.remove("is-active");
    nextActiveCard.classList.add("is-active");
    activeCardRef.current = nextActiveCard;
  }, [slider.trackRef]);

  useEffect(() => {
    const controller = new AbortController();

    api.testimonials
      .list({ signal: controller.signal })
      .then((response) => {
        setTestimonials(response.data || []);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setTestimonials([]);
        }
      });

    return () => controller.abort();
  }, []);

  const handleFeedbackChange = ({ target: { name, value } }) => {
    setFeedback((current) => ({
      ...current,
      [name]: name === "rating" ? Number(value) : value,
    }));
    setFeedbackError("");
    setFeedbackNotice("");
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    if (feedbackSubmitting) return;

    const name = feedback.name.trim();
    const content = feedback.content.trim();
    if (!name || !content) {
      setFeedbackError("Name and feedback are required.");
      return;
    }
    if (
      !Number.isInteger(feedback.rating) ||
      feedback.rating < 1 ||
      feedback.rating > 5
    ) {
      setFeedbackError("Rating must be between 1 and 5.");
      return;
    }

    setFeedbackSubmitting(true);
    setFeedbackError("");
    setFeedbackNotice("");
    try {
      await api.testimonials.create({
        name,
        content,
        rating: feedback.rating,
      });
      setFeedback(EMPTY_FEEDBACK);
      setFeedbackNotice(
        "Thank you. Your feedback will be reviewed before it appears.",
      );
    } catch (requestError) {
      setFeedbackError(
        requestError.message || "Feedback could not be submitted.",
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const openFeedbackPopup = () => {
    setFeedbackOpen(true);
    setFeedbackError("");
    setFeedbackNotice("");
  };

  const closeFeedbackPopup = () => {
    if (feedbackSubmitting) return;
    setFeedbackOpen(false);
  };

  useEffect(() => {
    if (!feedbackOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeFeedbackPopup();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [feedbackOpen, feedbackSubmitting]);

  useLayoutEffect(() => {
    const track = slider.trackRef.current;
    if (!track) return undefined;

    const scheduleActiveCardUpdate = () => {
      if (activeFrameRef.current !== null) return;

      activeFrameRef.current = requestAnimationFrame(() => {
        activeFrameRef.current = null;
        updateActiveCard();
      });
    };

    updateActiveCard();
    track.addEventListener("scroll", scheduleActiveCardUpdate, {
      passive: true,
    });
    window.addEventListener("resize", scheduleActiveCardUpdate);

    return () => {
      track.removeEventListener("scroll", scheduleActiveCardUpdate);
      window.removeEventListener("resize", scheduleActiveCardUpdate);
      if (activeFrameRef.current !== null) {
        cancelAnimationFrame(activeFrameRef.current);
        activeFrameRef.current = null;
      }
      activeCardRef.current = null;
    };
  }, [renderedTestimonials.length, slider.trackRef, updateActiveCard]);

  useLayoutEffect(() => {
    updateActiveCard();
  });

  useLayoutEffect(() => {
    const track = slider.trackRef.current;
    if (!track || !infiniteEnabled) return undefined;

    const setScrollLeftImmediately = (nextScrollLeft) => {
      if (!scrollStyleRestoreRef.current) {
        scrollStyleRestoreRef.current = {
          scrollBehavior: track.style.scrollBehavior,
          scrollSnapType: track.style.scrollSnapType,
        };
      }

      const restoreStyles = scrollStyleRestoreRef.current;

      track.style.scrollBehavior = "auto";
      track.style.scrollSnapType = "none";
      track.scrollLeft = nextScrollLeft;

      if (restoreFrameRef.current !== null) {
        cancelAnimationFrame(restoreFrameRef.current);
      }

      restoreFrameRef.current = requestAnimationFrame(() => {
        track.style.scrollBehavior = restoreStyles.scrollBehavior;
        track.style.scrollSnapType = restoreStyles.scrollSnapType;
        scrollStyleRestoreRef.current = null;
        restoreFrameRef.current = null;
        isCorrectingScrollRef.current = false;
      });
    };

    const measureGroupWidth = () => {
      const cards = track.querySelectorAll("[data-slider-item]");
      const firstCard = cards[0];
      const middleCard = cards[testimonials.length];
      if (!firstCard || !middleCard) return;

      const previousGroupWidth = loopWidthRef.current;
      const nextGroupWidth = middleCard.offsetLeft - firstCard.offsetLeft;
      if (!nextGroupWidth) return;

      const relativeOffset = previousGroupWidth
        ? getLoopOffset(track.scrollLeft, previousGroupWidth)
        : 0;
      const relativeRatio = previousGroupWidth
        ? relativeOffset / previousGroupWidth
        : 0;

      loopWidthRef.current = nextGroupWidth;
      isCorrectingScrollRef.current = true;
      setScrollLeftImmediately(nextGroupWidth + relativeRatio * nextGroupWidth);
    };

    const correctScrollPosition = () => {
      correctionFrameRef.current = null;

      const loopWidth = loopWidthRef.current;
      if (!loopWidth || isCorrectingScrollRef.current) return;

      const scrollLeft = track.scrollLeft;
      const isInsideMiddleGroup =
        scrollLeft >= loopWidth - LOOP_BOUNDARY_THRESHOLD &&
        scrollLeft <= loopWidth * 2 + LOOP_BOUNDARY_THRESHOLD;

      if (isInsideMiddleGroup) return;

      const relativeOffset = getLoopOffset(scrollLeft, loopWidth);
      const nextScrollLeft = loopWidth + relativeOffset;

      if (Math.abs(nextScrollLeft - scrollLeft) <= LOOP_BOUNDARY_THRESHOLD) {
        return;
      }

      isCorrectingScrollRef.current = true;
      setScrollLeftImmediately(nextScrollLeft);
    };

    const scheduleScrollCorrection = () => {
      if (correctionFrameRef.current !== null) return;
      correctionFrameRef.current = requestAnimationFrame(correctScrollPosition);
    };

    measureGroupWidth();
    track.addEventListener("scroll", scheduleScrollCorrection, {
      passive: true,
    });

    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(measureGroupWidth)
        : null;

    if (observer) {
      observer.observe(track);
    } else {
      window.addEventListener("resize", measureGroupWidth);
    }

    return () => {
      track.removeEventListener("scroll", scheduleScrollCorrection);
      observer?.disconnect();
      if (!observer) {
        window.removeEventListener("resize", measureGroupWidth);
      }
      if (correctionFrameRef.current !== null) {
        cancelAnimationFrame(correctionFrameRef.current);
        correctionFrameRef.current = null;
      }
      if (restoreFrameRef.current !== null) {
        cancelAnimationFrame(restoreFrameRef.current);
        restoreFrameRef.current = null;
      }
      if (scrollStyleRestoreRef.current) {
        track.style.scrollBehavior = scrollStyleRestoreRef.current.scrollBehavior;
        track.style.scrollSnapType = scrollStyleRestoreRef.current.scrollSnapType;
        scrollStyleRestoreRef.current = null;
      }
      loopWidthRef.current = 0;
      isCorrectingScrollRef.current = false;
    };
  }, [infiniteEnabled, slider.trackRef, testimonials.length]);

  return (
    <section className="testimonials">
      <img
        src={reviewBackground}
        alt=""
        aria-hidden="true"
        className="testimonials-bg"
      />
      <div className="testimonials-header">
        <h2 className="testimonials-title">Reviews</h2>
      </div>

      <div className="testimonials-wrapper">
        <div
          className={`testimonials-grid${infiniteEnabled ? " is-infinite" : ""}`}
          id="testimonials-slider"
          ref={slider.trackRef}
          role="list"
          aria-label="Customer testimonials"
          tabIndex={0}
          onKeyDown={slider.handleKeyDown}
        >
          {renderedTestimonials.map(
            ({ testimonial, groupIndex, originalIndex, key }) => (
              <div
                className="testimonial-card"
                key={key}
                role="listitem"
                data-slider-item
                data-testimonial-group={groupIndex}
                data-original-index={originalIndex}
              >
                <p className="testimonial-text">{testimonial.content}</p>
                <p className="testimonial-signature">{testimonial.name}</p>
                <span
                  className="testimonial-stars"
                  aria-label={`${testimonial.rating} star review`}
                >
                  {Array.from({ length: testimonial.rating }).map(
                    (_, starIndex) => (
                      <span aria-hidden="true" key={starIndex}>
                        &#9733;
                      </span>
                    ),
                  )}
                </span>
              </div>
            ),
          )}
        </div>
      </div>

      <span className="testimonials-mark">
        <img src={riseBeautiful} alt="" aria-hidden="true" />
        <span className="testimonials-mark-text">Rise Beautifully</span>
      </span>

      <div className="feedback-action">
        <button type="button" onClick={openFeedbackPopup}>
          Send testimony
        </button>
      </div>

      {feedbackOpen && (
        <div
          className="feedback-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-popup-title"
        >
          <button
            type="button"
            className="feedback-popup-backdrop"
            aria-label="Close feedback popup"
            onClick={closeFeedbackPopup}
          />
          <form className="feedback-form" onSubmit={submitFeedback}>
            <div className="feedback-form-header">
              <h3 id="feedback-popup-title">Send testimony</h3>
              <button
                type="button"
                className="feedback-popup-close"
                onClick={closeFeedbackPopup}
                aria-label="Close feedback popup"
              >
                &times;
              </button>
            </div>

            <div className="feedback-form-grid">
              <label>
                <span>Name</span>
                <input
                  type="text"
                  name="name"
                  value={feedback.name}
                  onChange={handleFeedbackChange}
                  maxLength={100}
                  required
                />
              </label>
              <label>
                <span>Rating</span>
                <select
                  name="rating"
                  value={feedback.rating}
                  onChange={handleFeedbackChange}
                >
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <option value={rating} key={rating}>
                      {rating}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              <span>Feedback</span>
              <textarea
                name="content"
                value={feedback.content}
                onChange={handleFeedbackChange}
                maxLength={2000}
                required
              />
            </label>

            {feedbackError && (
              <p className="feedback-form-error" role="alert">
                {feedbackError}
              </p>
            )}
            {feedbackNotice && (
              <p className="feedback-form-notice" role="status">
                {feedbackNotice}
              </p>
            )}

            <button
              type="submit"
              className="feedback-form-submit"
              disabled={feedbackSubmitting}
            >
              {feedbackSubmitting ? "Submitting..." : "Submit feedback"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

export default Testimonials;
